import type { GeographyApi } from "@App/apis/graphql/geography/interfaces";
import { NoLocationFoundError } from "@App/apis/graphql/geography/noLocationFoundError";
import { apolloClient } from "@App/graphql/client";
import { type GeoSuggestQuery, GET_GEOSUGGEST } from "@App/graphql/queries";
import type { Place } from "@App/types";
import { normalizeApolloError } from "@App/utils/api/graphql/normalizeApolloError";

/**
 * Service class implementing the GeographyApi interface.
 * Provides methods to interact with geographical data using GraphQL queries.
 */
export class GeographyService implements GeographyApi {
  /**
   * Retrieves a list of location suggestions based on a search query.
   *
   * @param search - The search string used to find location suggestions.
   * @returns A promise that resolves to an array of `Place` objects.
   * @throws ApolloError Will throw an error if the GraphQL query fails.
   */
  public async getLocationSuggests(search: string): Promise<Place[]> {
    const toSearch = search.trim();

    if (!toSearch) {
      return [];
    }

    const result = await apolloClient.query<GeoSuggestQuery>({
      query: GET_GEOSUGGEST,
      variables: {
        search: toSearch,
      },
    });

    const err = normalizeApolloError(result);
    if (err) throw err;

    return result.data.geosuggest;
  }

  /**
   * Resolves a house number location based on the provided address details.
   *
   * @param street - The street name of the address.
   * @param city - The city name of the address.
   * @param postcode - The postal code of the address.
   * @returns A promise that resolves to a `Place` object representing the house number location.
   * @throws NoLocationFoundError Will throw a `NoLocationFoundError` if no locations are found.
   * @throws ApolloError Will throw a `NoLocationFoundError` if no locations are found.
   */
  public async getHouseNumberLocation(
    street: string,
    city: string,
    postcode: string,
  ): Promise<Place> {
    const locations = await this.getLocationSuggests(`${street}, ${city} ${postcode}`);

    if (locations.length < 1) {
      throw new NoLocationFoundError("No location found");
    }

    return await this.getLocationByID(locations[0].id);
  }

  /**
   * Fetches location details by a given location ID.
   *
   * @param id - The unique identifier of the location.
   * @returns A promise that resolves to a `Place` object representing the location.
   * @throws ApolloError Will throw an error if the GraphQL query fails.
   */
  public async getLocationByID(id: string): Promise<Place> {
    const result = await apolloClient.query<GeoSuggestQuery>({
      query: GET_GEOSUGGEST,
      variables: {
        placeId: id,
      },
    });

    const err = normalizeApolloError(result);
    if (err) throw err;

    return result.data.geosuggest[0];
  }

  /**
   * Retrieves location details based on latitude and longitude coordinates.
   *
   * @param lat - The latitude of the location.
   * @param lon - The longitude of the location.
   * @returns A promise that resolves to a `Place` object representing the location.
   * @throws NoLocationFoundError Will throw a `NoLocationFoundError` if no locations are found.
   * @throws ApolloError Will throw an ApolloError when the GraphQL query fails.
   */
  public async getLocationByLatLon(lat: number, lon: number): Promise<Place> {
    const result = await apolloClient.query<GeoSuggestQuery>({
      query: GET_GEOSUGGEST,
      variables: {
        lat: lat,
        lon: lon,
      },
    });

    const err = normalizeApolloError(result);
    if (err) throw err;

    if (result.data.geosuggest.length < 1) {
      throw new NoLocationFoundError("No location found");
    }

    return await this.getLocationByID(result.data.geosuggest[0].id);
  }
}
